<template>
    <div class="jewelleries">
        <PageContentLoader v-if="fetching && !dataComputed" />

        <PageErrorFourHundredFour
            v-else-if="metal?.length && metal[0] === undefined"
        />

        <PageErrorFiveHundred
            v-else-if="error"
            :errors="responseErrors"
        >
            <ThButton
                :loading="fetching"
                @click="reloadPage"
            >
                {{ t('globals.try_again') }}
            </ThButton>
        </PageErrorFiveHundred>

        <div v-else-if="data && dataComputed">
            <ProductBreadcrumbs :breadcrumbs />

            <div
                v-qa="'before-filters-content'"
                class="before-filters-content"
            >
                <ThTitle
                    v-if="facetedTitle"
                    alignment="CENTER"
                    color="var(--blue)"
                    tag="H1"
                >
                    {{ facetedTitle }}
                </ThTitle>

                <CmsBlocks
                    v-else
                    :data="data.pageProductListing?.beforeFilters || []"
                />
            </div>

            <NameSearch v-model="search" />

            <ProductGrid
                ref="gridNode"
                :active-filter-count
                :banner1="data.pageProductListing?.banner1 || undefined"
                :banner2="data.pageProductListing?.banner2 || undefined"
                :data="dataComputed.items"
                :fetching
                :full-grid-at="perPage"
                :get-route-sort="filterRoutes.getRouteSort"
                :page
                :results-count="dataComputed.count"
                :sort-by="sortByWithDefault"
                :filter-chips
                :route-reset="filterRoutes.routeReset"
                :track-clicked
                @open-filters-modal="openFilterModal"
            />

            <Pagination
                :count="dataComputed.count"
                :current="page"
                :get-route-page="filterRoutes.getRoutePage"
                :limit="perPage"
                :range="2"
                scroll-into-view-selector=".product-grid"
            />

            <FacetedContent
                v-if="facetedContent"
                :content="facetedContent"
            />

            <CmsBlocks :data="data.pageProductListing?.afterGrid || []" />

            <JewelleryFilters
                v-if="isFilterModalRendered"
                v-qa="'jewellery-filters'"
                :filters="dataComputed.filters"
                :is-open="isFilterModalOpen"
                :loading="fetching"
                :product-types="jewelleryProductTypes"
                :result-count="dataComputed?.count"
                :selected-product-types="
                    productType ? [productType] : undefined
                "
                :filter-routes
                :chips="filterChips"
                :active-filter-count
                @close="closeFilterModal"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, hydrateOnVisible, toRef } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'

import {
    type CatalogueJewelleriesBraceletsFilterInput,
    type CatalogueJewelleriesEarringsFilterInput,
    type CatalogueJewelleriesFilterInput,
    type CatalogueJewelleriesNecklacesFilterInput,
    type CatalogueJewelleriesRingsFilterInput,
    CatalogueSortOrder,
    type JewelleryBraceletFiltersFragment,
    type JewelleryDesignStyle,
    type JewelleryEarringsFiltersFragment,
    type JewelleryFiltersFragment,
    type JewelleryGemstoneCode,
    type JewelleryNecklaceFiltersFragment,
    type JewelleryRingsFiltersFragment,
    type MetalCode,
} from '@/graphql/api'
import { type JewelleryProductStyle, type JewelleryProductType } from '@/router'

import { useMeta } from '@/composables/app/useMeta'
import { useProductListingPage } from '@/views/product/useProductListingPage'
import { useRoutes } from '@/router/routes'
import { getJewelleryBreadcrumbs } from '@/views/product/jewellery/getJewelleryBreadcrumbs.ts'
import { useProductListAnalytics } from '../composables/analytics/useProductListAnalytics'
import { useSearch } from '../composables/useSearch'
import { useFacetedContent } from '@/domain/product/useFacetedContent'

import { useJewelleriesFilters } from './useJewelleriesFilters'
import { useJewelleryData } from './useJewelleryData'
import { useLegacyJewelleryRedirect } from './useLegacyJewelleryRedirect'
import { useRoutePathWatcher } from '@/composables/route-path-watcher.ts'
import { usePageCount } from '@/composables/page-count.ts'
import { usePageWatcher } from '@/composables/page-watcher.ts'
import { useFocusFilterButton } from '../composables/ui/useFocusFilterButton'

import ThButton from '@/components/ui/buttons/ThButton.vue'
import PageErrorFiveHundred from '@/components/PageErrorFiveHundred.vue'
import ProductGrid from '@/components/rings/product-grid/ProductGrid.vue'
import ProductBreadcrumbs from '@/components/rings/ProductBreadcrumbs.vue'
import NameSearch from '@/components/rings/NameSearch.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import { useFilterModal } from '@/composables/filter-modal.ts'
import PageContentLoader from '@/components/ui/PageContentLoader.vue'
import { useFilterButtonClient } from '@/components/product/filter-button/useFilterButton'

const JewelleryFilters = defineAsyncComponent({
    loader: () => import('@/components/rings/jewellery/JewelleryFilters.vue'),
    hydrate: hydrateOnVisible(),
})

const PageErrorFourHundredFour = defineAsyncComponent({
    loader: () => import('@/components/PageErrorFourHundredFour.vue'),
    hydrate: hydrateOnVisible(),
})

const Pagination = defineAsyncComponent({
    loader: () => import('@/components/ui/Pagination.vue'),
    hydrate: hydrateOnVisible(),
})

const FacetedContent = defineAsyncComponent({
    loader: () => import('@/components/product/FacetedContent.vue'),
    hydrate: hydrateOnVisible(),
})

const CmsBlocks = defineAsyncComponent({
    loader: () => import('@/components/cms/CmsBlocks.vue'),
    hydrate: hydrateOnVisible(),
})

export type ComposeFilters<A, B> = {
    available: A
    selected: B
}

export type Filters =
    | ComposeFilters<JewelleryFiltersFragment, CatalogueJewelleriesFilterInput>
    | ComposeFilters<
          JewelleryEarringsFiltersFragment,
          CatalogueJewelleriesEarringsFilterInput
      >
    | ComposeFilters<
          JewelleryBraceletFiltersFragment,
          CatalogueJewelleriesBraceletsFilterInput
      >
    | ComposeFilters<
          JewelleryNecklaceFiltersFragment,
          CatalogueJewelleriesNecklacesFilterInput
      >
    | ComposeFilters<
          JewelleryRingsFiltersFragment,
          CatalogueJewelleriesRingsFilterInput
      >

export type FilterProductStylesAvailable = Exclude<
    Filters,
    ComposeFilters<JewelleryFiltersFragment, CatalogueJewelleriesFilterInput>
>['available']['productStyles'][number]

export type FilterProductStylesSelected = NonNullable<
    Exclude<
        Filters,
        ComposeFilters<
            JewelleryFiltersFragment,
            CatalogueJewelleriesFilterInput
        >
    >['selected']['productStyles']
>[number]

export type JewelleriesProps = {
    productType?: JewelleryProductType
    productStyle?: JewelleryProductStyle
    gemstone?: JewelleryGemstoneCode
    designStyle?: JewelleryDesignStyle
    metal?: MetalCode[] | undefined
    page: number
    priceMin?: number
    priceMax?: number
    legacyGemstones?: JewelleryGemstoneCode[]
    sortBy?: CatalogueSortOrder
}

const props = defineProps<JewelleriesProps>()

const routes = useRoutes()
const router = useRouter()

const pathWatcher = useRoutePathWatcher(
    () => {
        return router.resolve(
            routes.jewelleries({
                productType: props.productType,
                productStyle: props.productStyle,
                gemstone: props.gemstone,
                designStyle: props.designStyle,
                metal: props.metal?.[0],
                page: props.page,
            })
        )
    },
    () => {
        return router.resolve(
            routes.jewelleries({
                productType: props.productType,
                productStyle: props.productStyle,
                gemstone: props.gemstone,
                designStyle: props.designStyle,
                metal: props.metal?.[0],
                page: props.page,
                priceMin: props.priceMin,
                priceMax: props.priceMax,
                sortBy: props.sortBy,
            })
        ).fullPath
    },
    301
)

const legacyDesignFeaturesRedirect = useLegacyJewelleryRedirect(props)

const sortByWithDefault = toRef(
    () => props.sortBy ?? CatalogueSortOrder.Recommended
)

const { t } = useI18n()

const { filterRoutes, activeFilterCount, filterChips } = useJewelleriesFilters(
    props,
    toRef(() => dataComputed.value?.filters)
)

const {
    isFilterModalOpen,
    isFilterModalRendered,
    openFilterModal,
    closeFilterModal,
} = useFilterModal()

useFilterButtonClient(openFilterModal, activeFilterCount)

const { search } = useSearch(
    toRef(() => props.page),
    filterRoutes.getRoutePage
)

const {
    fetching,
    stale,
    data,
    dataComputed,
    error,
    reloadPage,
    responseErrors,
    perPage,
    jewelleryProductTypes,
    filterInput,
    activeFacetedFilterCount,
} = await useJewelleryData(
    props,
    filterRoutes.getRoutePrice,
    search,
    sortByWithDefault
)

const pageCount = usePageCount(
    () => dataComputed.value?.count ?? 0,
    () => perPage
)
const pageWatcher = usePageWatcher(
    () => props.page,
    pageCount,
    () => {
        return router.resolve(filterRoutes.getRoutePage(1)).fullPath
    },
    302
)

const {
    facetedTitle,
    facetedContent,
    facetedMetaDescription,
    facetedMetaTitle,
} = useFacetedContent(
    computed(() => ({
        type: 'jewelleries' as const,
        filters: dataComputed.value?.filters.available,
        filterValues: dataComputed.value?.filters.selected,
        productType: props.productType,
    })),
    toRef(() => data.value?.pageProductListingFacetedContent),
    activeFacetedFilterCount,
    toRef(() => data.value?.pageProductListing?.meta)
)

const catalogue = toRef(() => dataComputed.value?.items)

const facets = computed(() => {
    const metals: MetalCode[] | undefined = props.metal

    const productStyles =
        dataComputed.value &&
        'productStyles' in dataComputed.value.filters.available
            ? dataComputed.value.filters.available.productStyles
            : undefined

    return {
        productType: props.productType && {
            type: props.productType,
            totalResults: dataComputed.value?.count ?? 0,
        },
        metal:
            metals &&
            dataComputed.value?.filters.available?.primaryMetals.filter((v) =>
                metals.includes(v.metal.systemCode)
            ),
        gemstone: dataComputed.value?.filters.available.gemstones?.find(
            (s) => s.label.systemCode === props.gemstone
        ),
        productStyle: productStyles?.find(
            (s) => s.label.systemCode === props.productStyle
        ),
        designStyle: dataComputed.value?.filters.available.designStyles?.find(
            (s) => s.label.systemCode === props.designStyle
        ),
    }
})

const { trackClicked } = useProductListAnalytics(
    catalogue,
    filterInput,
    computed(() => [
        facets.value.metal?.[0]?.metal?.translation.name,
        facets.value.gemstone?.label.translation.name,
        facets.value.designStyle?.label.translation.name,
        facets.value.productStyle?.label.translation.name,
    ]),
    sortByWithDefault,
    toRef(() => props.page),
    computed(() => ({
        productType: props.productType,
    }))
)

const { breadcrumbs } = useProductListingPage({
    facets,
    resetRouteGetter: (facets) =>
        routes.jewelleries({
            productType: facets.productType?.type,
            metal: facets.metal?.[0]?.metal.systemCode,
            gemstone: facets.gemstone?.label.systemCode,
            productStyle: facets.productStyle?.label.systemCode,
            designStyle: facets.designStyle?.label.systemCode,
        }),
    breadcrumbsGetter: (facets) =>
        getJewelleryBreadcrumbs(
            {
                productType: computed(() => facets.value.productType?.type),
                productStyle: computed(() => facets.value.productStyle?.label),
                gemstone: computed(() => facets.value.gemstone?.label),
                designStyle: computed(() => facets.value.designStyle?.label),
                metal: computed(
                    () => facets.value.metal?.map((item) => item.metal) || []
                ),
                product: undefined,
            },
            routes
        ),
})

useMeta(
    {
        title: () => facetedMetaTitle.value,
        description: () => facetedMetaDescription.value,
    },
    data,
    fetching,
    stale
)

useHead({
    meta: () => {
        if (activeFacetedFilterCount.value > 2) {
            return [
                {
                    name: 'robots',
                    content: 'noindex, nofollow',
                },
            ]
        }

        return []
    },
})

const { gridNode } = useFocusFilterButton(isFilterModalOpen)

await pathWatcher()

await pageWatcher()

if (props.legacyGemstones?.length) {
    await legacyDesignFeaturesRedirect()
}
</script>
